/**
 * Offline-first IndexedDB storage for driver workflows.
 * 
 * Tables:
 * - jobs_cache: cached assigned jobs for offline viewing
 * - job_drafts: POC/POD draft data persisted across app kills
 * - sync_queue: actions queued for server sync
 * - app_state: key-value store for sync metadata
 */

import { logOfflineEvent } from "@/lib/offlineTelemetry";
import { captureEvent } from "@/lib/observability";

const DB_NAME = "transportmate_offline";
const DB_VERSION = 1;

let dbInstance: IDBDatabase | null = null;
let opening: Promise<IDBDatabase> | null = null;

/** Drop the cached handle so the next operation reopens a fresh connection. */
function resetConnection(reason: string) {
  if (dbInstance) {
    console.warn(`[OfflineDB] Connection dropped (${reason}) — will reopen on next access`);
  }
  dbInstance = null;
  opening = null;
}

// ─── Open / Upgrade ──────────────────────────────────────────
function openDb(): Promise<IDBDatabase> {
  if (dbInstance) return Promise.resolve(dbInstance);
  if (opening) return opening;

  opening = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = () => {
      const db = req.result;

      // jobs_cache
      if (!db.objectStoreNames.contains("jobs_cache")) {
        db.createObjectStore("jobs_cache", { keyPath: "job_id" });
      }

      // job_drafts
      if (!db.objectStoreNames.contains("job_drafts")) {
        db.createObjectStore("job_drafts", { keyPath: "job_id" });
      }

      // sync_queue – ordered by created_at
      if (!db.objectStoreNames.contains("sync_queue")) {
        const sq = db.createObjectStore("sync_queue", { keyPath: "id" });
        sq.createIndex("by_next_retry", "next_retry_at", { unique: false });
        sq.createIndex("by_job", "job_id", { unique: false });
      }

      // app_state
      if (!db.objectStoreNames.contains("app_state")) {
        db.createObjectStore("app_state", { keyPath: "key" });
      }
    };

    req.onsuccess = () => {
      dbInstance = req.result;
      // iOS/WKWebView can close the connection while backgrounded. Without
      // these handlers the stale handle is reused forever and every write
      // (including queued completions) fails silently.
      dbInstance.onclose = () => resetConnection("onclose");
      dbInstance.onversionchange = () => {
        try { dbInstance?.close(); } catch { /* ignore */ }
        resetConnection("versionchange");
      };
      dbInstance.onerror = () => resetConnection("db error");
      resolve(dbInstance);
    };

    req.onerror = () => {
      opening = null;
      reject(req.error);
    };
  });

  return opening;
}

/**
 * iOS WebKit reports a lost backend transaction with this exact message
 * (confirmed on 1.74, ALW General Haulage, 2026-09-25). It is recoverable by
 * reopening the connection, exactly like a closing/closed handle.
 */
const LOST_TRANSACTION_PATTERN = /without an in-progress transaction/i;

/** True when a fresh connection may fix the failure (stale / lost handle). */
export function isRecoverableDbError(err: unknown): boolean {
  const e = err as { name?: string; message?: string } | null | undefined;
  const msg = e?.message || String(err ?? "");
  return (
    e?.name === "InvalidStateError" ||
    e?.name === "TransactionInactiveError" ||
    /closing|closed|InvalidStateError/i.test(msg) ||
    LOST_TRANSACTION_PATTERN.test(msg)
  );
}

// ─── Recovery diagnostics (rate-limited, no payload data) ────
const RECOVERY_TELEMETRY_WINDOW_MS = 60_000;
const recoveryTelemetryLast = new Map<string, number>();
const recoveryTelemetrySuppressed = new Map<string, number>();
let recoveryAttemptCount = 0;

/** Safe error descriptor: name, message, truncated stack. Never payloads. */
export function describeDbError(err: unknown): { error_name: string; error_message: string; error_stack: string | null } {
  const e = err as { name?: string; message?: string; stack?: string } | null | undefined;
  return {
    error_name: e?.name || "UnknownError",
    // Diagnostic fallback only: a null/empty reason is labelled explicitly.
    error_message:
      String(e?.message || err || "").slice(0, 300) ||
      `no error details provided (${err === null ? "null" : err === undefined ? "undefined" : typeof err})`,
    error_stack: e?.stack ? String(e.stack).slice(0, 1500) : null,
  };
}

// ─── 1.0.x — storage recovery containment ────────────────────
// When the existing reopen+retry recovery itself fails, background work that
// depends on IndexedDB (upload drain, sync interval) pauses instead of
// re-hitting a broken storage engine every few seconds. Nothing is deleted or
// marked failed; foreground capture and explicit user actions are unaffected.
// There is no timer: existing polls ask `shouldRunBackgroundStorageWork()`,
// which allows one probe per backoff window (15s → 30s → … → 5 min).
// Resumes on: app foreground / explicit user retry / online, or any
// successful IndexedDB operation or recovery.
const PAUSE_BACKOFF_BASE_MS = 15_000;
const PAUSE_BACKOFF_MAX_MS = 5 * 60_000;
/**
 * 1.0.x (2026-10-02, 7da6c2fc incident) — escalation memory. The backoff level
 * survives an automatic resume and only decays after this long with no
 * recovery failure, so an intermittently-failing storage engine climbs to the
 * 5-min ceiling instead of restarting at 15s after every lucky success.
 */
const PAUSE_ESCALATION_DECAY_MS = 2 * PAUSE_BACKOFF_MAX_MS;
let storagePaused = false;
let pauseBackoffMs = 0;
let pauseProbeAt = 0;
let pausedAt = 0;
let lastPauseFailureAt = 0;
/** Recovery-failed keys already reported during the current pause. */
const reportedDuringPause = new Set<string>();

export function isStorageRecoveryPaused(): boolean {
  return storagePaused;
}

/** True when background IndexedDB work may run (not paused, or probe due). */
export function shouldRunBackgroundStorageWork(now = Date.now()): boolean {
  return !storagePaused || now >= pauseProbeAt;
}

function enterStorageRecoveryPause(op: string, errorName: string) {
  const now = Date.now();
  if (pauseBackoffMs && lastPauseFailureAt && now - lastPauseFailureAt > PAUSE_ESCALATION_DECAY_MS) {
    pauseBackoffMs = 0; // healthy long enough: start the ladder again
  }
  lastPauseFailureAt = now;
  pauseBackoffMs = pauseBackoffMs ? Math.min(pauseBackoffMs * 2, PAUSE_BACKOFF_MAX_MS) : PAUSE_BACKOFF_BASE_MS;
  pauseProbeAt = now + pauseBackoffMs;
  if (storagePaused) return; // already paused: only the probe window extends
  storagePaused = true;
  pausedAt = now;
  try {
    captureEvent({
      kind: "error",
      severity: "warn",
      component: "offline.db_recovery",
      message: "storage_recovery_paused",
      metadata: { operation: op, error_name: errorName, backoff_ms: pauseBackoffMs },
    });
  } catch { /* telemetry must never break storage */ }
}

function leavePause(trigger: string) {
  storagePaused = false;
  pauseProbeAt = 0;
  reportedDuringPause.clear();
  try {
    captureEvent({
      kind: "performance",
      severity: "info",
      component: "offline.db_recovery",
      message: "storage_recovery_resumed",
      metadata: { trigger, paused_ms: Date.now() - pausedAt, backoff_level_ms: pauseBackoffMs },
    });
  } catch { /* telemetry must never break storage */ }
}

/**
 * Explicit resume (foreground / online / user retry). Leaves the pause
 * immediately and resets the escalation ladder. No-op when not paused.
 */
export function resumeStorageRecovery(trigger: string): void {
  if (!storagePaused) { pauseBackoffMs = 0; lastPauseFailureAt = 0; return; }
  pauseBackoffMs = 0;
  lastPauseFailureAt = 0;
  leavePause(trigger);
}

/**
 * Automatic resume after an IndexedDB success. An unrelated success inside the
 * current probe window (another store, the other DB, a lucky one-off retry)
 * does NOT lift the pause — previously it did, resetting the backoff to 0 and
 * letting the 10s/15s polls hit the broken engine again (pause/resume flap).
 * Once the probe window has elapsed a success lifts the pause, but the
 * escalation level is kept so a further failure backs off longer.
 */
function autoResumeStorageRecovery(trigger: string, now = Date.now()): void {
  if (!storagePaused) return;
  if (now < pauseProbeAt) return;
  leavePause(trigger);
}

/** Called by both IndexedDB wrappers after a first-try success. */
export function noteStorageOperationSucceeded(): void {
  autoResumeStorageRecovery("storage_operation_succeeded");
}

export function __resetStorageRecoveryForTests() {
  storagePaused = false; pauseBackoffMs = 0; pauseProbeAt = 0; pausedAt = 0; lastPauseFailureAt = 0;
  reportedDuringPause.clear();
  recoveryTelemetryLast.clear();
  recoveryTelemetrySuppressed.clear();
}

export function __getStorageRecoveryStateForTests() {
  return { storagePaused, pauseBackoffMs, pauseProbeAt, lastPauseFailureAt };
}

export function reportRecovery(
  outcome: "indexeddb_recovery_succeeded" | "indexeddb_recovery_failed",
  op: string,
  original: unknown,
  retryError?: unknown,
) {
  try {
    const orig = describeDbError(original);
    const failedOutcome = outcome === "indexeddb_recovery_failed";
    const key = `${outcome}|${op}|${orig.error_name}|${orig.error_message}`;
    if (failedOutcome) {
      // A repeat of the same failure while already paused is the same
      // underlying condition: count it, don't re-report it.
      if (storagePaused && reportedDuringPause.has(key)) {
        recoveryTelemetrySuppressed.set(key, (recoveryTelemetrySuppressed.get(key) ?? 0) + 1);
        enterStorageRecoveryPause(op, orig.error_name);
        return;
      }
    } else {
      autoResumeStorageRecovery("recovery_succeeded");
    }
    const now = Date.now();
    const last = recoveryTelemetryLast.get(key) ?? 0;
    if (now - last < RECOVERY_TELEMETRY_WINDOW_MS) {
      recoveryTelemetrySuppressed.set(key, (recoveryTelemetrySuppressed.get(key) ?? 0) + 1);
      if (failedOutcome) { reportedDuringPause.add(key); enterStorageRecoveryPause(op, orig.error_name); }
      return;
    }
    const suppressed = recoveryTelemetrySuppressed.get(key) ?? 0;
    recoveryTelemetryLast.set(key, now);
    recoveryTelemetrySuppressed.delete(key);
    const failed = failedOutcome;
    captureEvent({
      kind: failed ? "error" : "performance",
      severity: failed ? "warn" : "info",
      component: "offline.db_recovery",
      message: outcome,
      stack: orig.error_stack,
      metadata: {
        operation: op,
        recovery_attempt: recoveryAttemptCount,
        reconnect_succeeded: !failed,
        ...orig,
        ...(retryError !== undefined
          ? {
              retry_error_name: describeDbError(retryError).error_name,
              retry_error_message: describeDbError(retryError).error_message,
            }
          : {}),
        suppressed_since_last: suppressed,
      },
    });
    if (failed) {
      reportedDuringPause.add(key);
      enterStorageRecoveryPause(op, orig.error_name);
    }
  } catch { /* telemetry must never break storage */ }
}

/**
 * Run an IndexedDB operation, reopening the connection once if the handle has
 * gone stale ("database connection is closing" / InvalidStateError) or the
 * backend transaction was lost. Only ever retries the same operation once;
 * a failed retry is rethrown unchanged so callers keep their data intact.
 */
async function withDb<T>(fn: (db: IDBDatabase) => Promise<T>, op = "unknown"): Promise<T> {
  try {
    const firstTry = await fn(await openDb());
    noteStorageOperationSucceeded();
    return firstTry;
  } catch (err: any) {
    if (!isRecoverableDbError(err)) throw err;
    const msg = err?.message || String(err);
    recoveryAttemptCount += 1;
    // Abandon the broken handle so the next open creates a fresh connection.
    const stale = dbInstance;
    resetConnection(`retry after: ${msg}`);
    try { stale?.close(); } catch { /* ignore */ }
    try {
      const result = await fn(await openDb());
      reportRecovery("indexeddb_recovery_succeeded", op, err);
      return result;
    } catch (retryErr) {
      resetConnection(`retry failed: ${(retryErr as any)?.message || String(retryErr)}`);
      reportRecovery("indexeddb_recovery_failed", op, err, retryErr);
      throw retryErr;
    }
  }
}

// ─── Generic helpers ─────────────────────────────────────────
async function getAll<T>(store: string): Promise<T[]> {
  return withDb((db) => new Promise<T[]>((resolve, reject) => {
    const tx = db.transaction(store, "readonly");
    const s = tx.objectStore(store);
    const req = s.getAll();
    req.onsuccess = () => resolve(req.result as T[]);
    req.onerror = () => reject(req.error);
  }), `getAll:${store}`);
}

async function getByKey<T>(store: string, key: string): Promise<T | undefined> {
  return withDb((db) => new Promise<T | undefined>((resolve, reject) => {
    const tx = db.transaction(store, "readonly");
    const s = tx.objectStore(store);
    const req = s.get(key);
    req.onsuccess = () => resolve(req.result as T | undefined);
    req.onerror = () => reject(req.error);
  }), `get:${store}`);
}

async function put<T>(store: string, value: T): Promise<void> {
  const startedAt = Date.now();
  const record = value as unknown as Record<string, unknown>;
  const ids = {
    jobId: typeof record?.job_id === "string" ? record.job_id : undefined,
    subject: typeof record?.type === "string" ? (record.type as string) : undefined,
  };
  try {
    await withDb((db) => new Promise<void>((resolve, reject) => {
      const tx = db.transaction(store, "readwrite");
      const s = tx.objectStore(store);
      s.put(value);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error || new Error("Transaction aborted"));
    }), `put:${store}`);
    logOfflineEvent("indexeddb_write_success", { ...ids, store, durationMs: Date.now() - startedAt });
  } catch (error) {
    logOfflineEvent("indexeddb_write_failure", { ...ids, store, durationMs: Date.now() - startedAt, error });
    throw error;
  }
}

async function deleteByKey(store: string, key: string): Promise<void> {
  return withDb((db) => new Promise<void>((resolve, reject) => {
    const tx = db.transaction(store, "readwrite");
    const s = tx.objectStore(store);
    s.delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error("Transaction aborted"));
  }), `delete:${store}`);
}

async function clearStore(store: string): Promise<void> {
  return withDb((db) => new Promise<void>((resolve, reject) => {
    const tx = db.transaction(store, "readwrite");
    const s = tx.objectStore(store);
    s.clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error("Transaction aborted"));
  }), `clear:${store}`);
}


// ─── jobs_cache ──────────────────────────────────────────────

export interface CachedJob {
  job_id: string;
  company_id: string;
  assigned_user_id: string;
  /** Full job object JSON-serialised */
  payload_json: string;
  updated_at: number;
  etag?: string;
}

export const jobsCache = {
  getAll: () => getAll<CachedJob>("jobs_cache"),
  get: (jobId: string) => getByKey<CachedJob>("jobs_cache", jobId),
  put: (job: CachedJob) => put("jobs_cache", job),
  delete: (jobId: string) => deleteByKey("jobs_cache", jobId),
  clear: () => clearStore("jobs_cache"),

  /** Bulk-replace all cached jobs (after a full sync) */
  // Routed through withDb (shared 8155d73e recovery). clear + puts stay in ONE
  // transaction, so a lost/aborted transaction rolls back atomically and the
  // single retry re-runs the whole replacement — never a half-empty cache.
  async replaceAll(jobs: CachedJob[]): Promise<void> {
    return withDb((db) => new Promise<void>((resolve, reject) => {
      const tx = db.transaction("jobs_cache", "readwrite");
      const s = tx.objectStore("jobs_cache");
      s.clear();
      for (const j of jobs) s.put(j);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error || new Error("Transaction aborted"));
    }), "replaceAll:jobs_cache");
  },
};

// ─── job_drafts ──────────────────────────────────────────────

export type DraftStatus =
  | "draft_poc"
  | "draft_pod"
  | "poc_completed_pending_sync"
  | "pod_completed_pending_sync"
  | "complete";

export interface JobDraft {
  job_id: string;
  company_id: string;
  poc_json: string | null;
  pod_json: string | null;
  poc_signature_path: string | null;
  pod_signature_path: string | null;
  status: DraftStatus;
  last_saved_at: number;
}

export const jobDrafts = {
  getAll: () => getAll<JobDraft>("job_drafts"),
  get: (jobId: string) => getByKey<JobDraft>("job_drafts", jobId),
  put: (draft: JobDraft) => put("job_drafts", draft),
  delete: (jobId: string) => deleteByKey("job_drafts", jobId),
  clear: () => clearStore("job_drafts"),
};

// ─── sync_queue ──────────────────────────────────────────────

export type SyncActionType =
  | "UPSERT_POC"
  | "UPSERT_POD"
  | "UPSERT_PART_INSPECTION"
  | "UPLOAD_SIGNATURE"
  | "UPLOAD_PHOTO"
  | "COMPLETE_POC"
  | "COMPLETE_POD";


export interface SyncQueueItem {
  id: string;
  job_id: string;
  company_id: string;
  type: SyncActionType;
  payload_json: string;
  /** Base64 data URL for signatures/photos */
  file_data: string | null;
  attempts: number;
  next_retry_at: number;
  created_at: number;
  last_error: string | null;
}

/** Queue item routing metadata without payloads (see `syncQueue.getReadySummaries`). */
export type SyncQueueSummary = Omit<SyncQueueItem, "payload_json" | "file_data">;

export const syncQueue = {
  getAll: () => getAll<SyncQueueItem>("sync_queue"),
  get: (id: string) => getByKey<SyncQueueItem>("sync_queue", id),
  put: (item: SyncQueueItem) => put("sync_queue", item),
  delete: (id: string) => deleteByKey("sync_queue", id),
  clear: () => clearStore("sync_queue"),

  /** Get items ready to retry (next_retry_at <= now), ordered ascending */
  async getReady(): Promise<SyncQueueItem[]> {
    const all = await getAll<SyncQueueItem>("sync_queue");
    const now = Date.now();
    return all
      .filter((i) => i.next_retry_at <= now)
      .sort((a, b) => a.created_at - b.created_at);
  },

  /**
   * Enqueue a new sync action.
   * The write is verified by reading the row back — a silently dropped write
   * (stale IndexedDB handle) must never look like a successful queue.
   */
  async enqueue(
    params: Omit<SyncQueueItem, "id" | "attempts" | "next_retry_at" | "created_at" | "last_error">
  ): Promise<string> {
    const id = crypto.randomUUID();
    const item: SyncQueueItem = {
      ...params,
      id,
      attempts: 0,
      next_retry_at: Date.now(),
      created_at: Date.now(),
      last_error: null,
    };
    try {
      await put("sync_queue", item);
    } catch (error) {
      logOfflineEvent("queue_enqueue_failure", {
        jobId: item.job_id,
        store: "sync_queue",
        subject: item.type,
        error,
      });
      throw error;
    }
    const persisted = await getByKey<SyncQueueItem>("sync_queue", id);
    if (!persisted) {
      logOfflineEvent("queue_enqueue_failure", {
        jobId: item.job_id,
        store: "sync_queue",
        subject: item.type,
        error: "write_not_persisted",
      });
      try {
        const { captureEvent } = await import("@/lib/observability");
        captureEvent({ kind: "error", component: "sync.queue", message: "enqueue_not_persisted", metadata: { type: item.type, jobId: item.job_id } });
      } catch { /* telemetry must never mask the failure */ }
      throw new Error(`Sync queue write was not persisted (${item.type})`);
    }
    logOfflineEvent("queue_enqueue_success", {
      jobId: item.job_id,
      store: "sync_queue",
      subject: item.type,
      queueItemId: id,
      bytes: item.file_data ? item.file_data.length : 0,
    });
    console.log(`[OfflineDB] queued ${item.type} id=${id.slice(0, 8)} job=${item.job_id.slice(0, 8)}`);
    return id;
  },


  /** Mark an item as retried with exponential backoff */
  async markRetry(id: string, error: string): Promise<void> {
    const item = await getByKey<SyncQueueItem>("sync_queue", id);
    if (!item) return;
    item.attempts += 1;
    const delay = Math.min(Math.pow(2, item.attempts) * 5000, 5 * 60 * 1000);
    item.next_retry_at = Date.now() + delay;
    item.last_error = error;
    await put("sync_queue", item);
  },

  /**
   * iOS 18.7 hotfix — ready items WITHOUT their payloads, oldest first. One
   * cursor pass that keeps only routing metadata (payload_json / file_data —
   * the large base64 signatures — are dropped as the cursor passes), so the
   * pass never holds the whole queue in memory. Read each item in full with
   * `get(id)` immediately before processing it.
   */
  async getReadySummaries(now = Date.now()): Promise<SyncQueueSummary[]> {
    return withDb((db) => new Promise<SyncQueueSummary[]>((resolve, reject) => {
      const out: SyncQueueSummary[] = [];
      const tx = db.transaction("sync_queue", "readonly");
      const req = tx.objectStore("sync_queue").openCursor();
      req.onsuccess = () => {
        const cursor = req.result;
        if (!cursor) return;
        const { payload_json: _p, file_data: _f, ...meta } = cursor.value as SyncQueueItem;
        if (meta.next_retry_at <= now) out.push(meta);
        cursor.continue();
      };
      req.onerror = () => reject(req.error);
      tx.oncomplete = () => resolve(out.sort((a, b) => a.created_at - b.created_at));
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error || new Error("Sync queue transaction aborted"));
    }), "getReadySummaries:sync_queue");
  },

  /** Get count of pending items (IDB count — no records are read). */
  async pendingCount(): Promise<number> {
    return withDb((db) => new Promise<number>((resolve, reject) => {
      const req = db.transaction("sync_queue", "readonly").objectStore("sync_queue").count();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    }), "count:sync_queue");
  },
};

// ─── app_state ───────────────────────────────────────────────

interface AppStateEntry {
  key: string;
  value: string;
}

export const appState = {
  async get(key: string): Promise<string | null> {
    const entry = await getByKey<AppStateEntry>("app_state", key);
    return entry?.value ?? null;
  },
  async set(key: string, value: string): Promise<void> {
    await put("app_state", { key, value });
  },
  async delete(key: string): Promise<void> {
    await deleteByKey("app_state", key);
  },
  /**
   * Non-essential "last run" timestamp write. Skipped (not queued) while the
   * document is hidden: iOS WebKit frequently loses the IndexedDB transaction
   * during background transitions and app_state was the first failing store
   * in 17/28 episodes. Essential app_state keys must keep using set().
   */
  async setNonEssential(key: string, value: string): Promise<boolean> {
    if (isAppBackgrounded()) return false;
    await put("app_state", { key, value });
    return true;
  },
};

/** True only when the document positively reports it is hidden. */
export function isAppBackgrounded(): boolean {
  return typeof document !== "undefined" && document.visibilityState === "hidden";
}

// ─── Initialisation ──────────────────────────────────────────

/** Pre-warm the database connection */
export async function initOfflineDb(): Promise<void> {
  await openDb();
  console.log("[OfflineDB] Initialised");
}
